/**
 * What each plan may do. Kept in one place so the server, the client and the
 * tests agree.
 *
 * - free_trial: everything, until the trial end date.
 * - standard:   everything (paid subscription).
 * - free:       symbols, keyboard and personalisation keep working, but
 *               speech output is disabled (as in the previous version).
 *
 * Speech synthesis runs in the browser, so the voice restriction is enforced
 * in the client; the server exposes `canUseVoice` in /api/auth/me so the rule
 * lives in a single place.
 */
import type { PaymentStatus, Plan } from './constants';

export interface PlanInfo {
  plan: Plan;
  paymentStatus: PaymentStatus;
  trialEndsAt: string | Date | null;
}

const DAY_MS = 24 * 60 * 60 * 1000;

export function isTrialExpired(info: PlanInfo, now = new Date()): boolean {
  if (info.plan !== 'free_trial' || !info.trialEndsAt) return false;
  return new Date(info.trialEndsAt).getTime() < now.getTime();
}

/** The plan that is actually in force, accounting for an expired trial. */
export function effectivePlan(info: PlanInfo, now = new Date()): Plan {
  return isTrialExpired(info, now) ? 'free' : info.plan;
}

export function canUseVoice(info: PlanInfo, now = new Date()): boolean {
  return effectivePlan(info, now) !== 'free';
}

/** Whole days left in the trial (0 when it ended or the user is not on trial). */
export function trialDaysLeft(info: PlanInfo, now = new Date()): number {
  if (info.plan !== 'free_trial' || !info.trialEndsAt) return 0;
  return Math.max(0, Math.ceil((new Date(info.trialEndsAt).getTime() - now.getTime()) / DAY_MS));
}
